import type { QdsConfigField, QdsPropertyFieldPluginElement } from '../../types/blocks.js';
import { loadBaseCSS } from '../shared/button-utils.js';

/**
 * @element qds-color-picker
 * @property {string} property - Field name emitted with the selected color
 * @property {string} value - Current color value
 * @property {object} metadata - Property-editor metadata
 * @property {object} context - Optional host context
 * @property {object} target - Optional object currently being edited
 * @property {boolean} readonly - Disables editing controls
 * @fires value-change - Selected color ({ property, value })
 */

interface ThemeColorOption {
  label: string;
  token: string;
  value: string;
}

const DEFAULT_COLOR_VALUE = 'var(--theme-accent-primary)';
const DEFAULT_COLOR_HEX = '#445E89';
const THEME_COLOR_OPTIONS: ThemeColorOption[] = [
  { label: 'Background Primary', token: '--theme-background-primary', value: 'var(--theme-background-primary)' },
  { label: 'Background Secondary', token: '--theme-background-secondary', value: 'var(--theme-background-secondary)' },
  { label: 'Background Tertiary', token: '--theme-background-tertiary', value: 'var(--theme-background-tertiary)' },
  { label: 'Background Accent Subtle', token: '--theme-background-accent-subtle', value: 'var(--theme-background-accent-subtle)' },
  { label: 'Background Success Subtle', token: '--theme-background-success-subtle', value: 'var(--theme-background-success-subtle)' },
  { label: 'Background Warning Subtle', token: '--theme-background-warning-subtle', value: 'var(--theme-background-warning-subtle)' },
  { label: 'Background Error Subtle', token: '--theme-background-error-subtle', value: 'var(--theme-background-error-subtle)' },
  { label: 'Background Info Subtle', token: '--theme-background-info-subtle', value: 'var(--theme-background-info-subtle)' },
  { label: 'Text Primary', token: '--theme-text-primary', value: 'var(--theme-text-primary)' },
  { label: 'Text Secondary', token: '--theme-text-secondary', value: 'var(--theme-text-secondary)' },
  { label: 'Text Tertiary', token: '--theme-text-tertiary', value: 'var(--theme-text-tertiary)' },
  { label: 'Text Inverse', token: '--theme-text-inverse', value: 'var(--theme-text-inverse)' },
  { label: 'Text On Accent Primary', token: '--theme-text-on-accent-primary', value: 'var(--theme-text-on-accent-primary)' },
  { label: 'Text On Accent Secondary', token: '--theme-text-on-accent-secondary', value: 'var(--theme-text-on-accent-secondary)' },
  { label: 'Text On Success', token: '--theme-text-on-success', value: 'var(--theme-text-on-success)' },
  { label: 'Text On Warning', token: '--theme-text-on-warning', value: 'var(--theme-text-on-warning)' },
  { label: 'Text On Error', token: '--theme-text-on-error', value: 'var(--theme-text-on-error)' },
  { label: 'Text On Info', token: '--theme-text-on-info', value: 'var(--theme-text-on-info)' },
  { label: 'Border Primary', token: '--theme-border-primary', value: 'var(--theme-border-primary)' },
  { label: 'Border Secondary', token: '--theme-border-secondary', value: 'var(--theme-border-secondary)' },
  { label: 'Border Strong', token: '--theme-border-strong', value: 'var(--theme-border-strong)' },
  { label: 'Accent Primary', token: '--theme-accent-primary', value: 'var(--theme-accent-primary)' },
  { label: 'Accent Secondary', token: '--theme-accent-secondary', value: 'var(--theme-accent-secondary)' },
  { label: 'Success', token: '--theme-success', value: 'var(--theme-success)' },
  { label: 'Warning', token: '--theme-warning', value: 'var(--theme-warning)' },
  { label: 'Error', token: '--theme-error', value: 'var(--theme-error)' },
  { label: 'Info', token: '--theme-info', value: 'var(--theme-info)' }
];

loadBaseCSS();

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function extractCssVariableName(value: string): string | null {
  const match = value.trim().match(/^var\(\s*(--[A-Za-z0-9-_]+)/);
  return match ? match[1] : null;
}

function normalizeHexColor(value: string): string | null {
  const trimmed = value.trim();
  const longHexMatch = trimmed.match(/^#([0-9a-fA-F]{6})$/);
  if (longHexMatch) {
    return `#${longHexMatch[1].toUpperCase()}`;
  }

  const shortHexMatch = trimmed.match(/^#([0-9a-fA-F]{3})$/);
  if (shortHexMatch) {
    const expanded = shortHexMatch[1]
      .split('')
      .map((channel) => `${channel}${channel}`)
      .join('');
    return `#${expanded.toUpperCase()}`;
  }

  const rgbMatch = trimmed.match(/^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})/i);
  if (rgbMatch) {
    const hexChannels = rgbMatch.slice(1, 4).map((channel) => {
      const clamped = Math.max(0, Math.min(255, Number.parseInt(channel, 10)));
      return clamped.toString(16).padStart(2, '0');
    });
    return `#${hexChannels.join('').toUpperCase()}`;
  }

  return null;
}

function resolveColorExpression(value: string): string | null {
  if (!value || typeof document === 'undefined' || !document.body) {
    return null;
  }

  const probe = document.createElement('span');
  probe.style.color = value;
  probe.style.display = 'none';
  document.body.appendChild(probe);
  const resolved = getComputedStyle(probe).color;
  probe.remove();
  return resolved || null;
}

export class QdsColorPicker extends HTMLElement implements QdsPropertyFieldPluginElement {
  private _property?: string;
  private _value: unknown = DEFAULT_COLOR_VALUE;
  private _metadata?: QdsConfigField;
  private _context?: unknown;
  private _target?: Record<string, unknown>;
  private _readonly = false;
  private _open = false;
  private _draftValue = DEFAULT_COLOR_VALUE;
  private _activeTab: 'theme' | 'custom' = 'theme';
  private readonly _handleClickBound: EventListener;
  private readonly _handleInputBound: EventListener;
  private readonly _handleChangeBound: EventListener;
  private readonly _handleKeydownBound: EventListener;

  static get observedAttributes(): string[] {
    return ['value', 'readonly'];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this._handleClickBound = (event: Event) => this._handleClick(event as MouseEvent);
    this._handleInputBound = (event: Event) => this._handleInput(event);
    this._handleChangeBound = (event: Event) => this._handleChange(event);
    this._handleKeydownBound = (event: Event) => this._handleKeydown(event as KeyboardEvent);
    this.render();
  }

  connectedCallback(): void {
    if (!this.shadowRoot) {
      return;
    }

    this.shadowRoot.addEventListener('click', this._handleClickBound);
    this.shadowRoot.addEventListener('input', this._handleInputBound);
    this.shadowRoot.addEventListener('change', this._handleChangeBound);
    this.shadowRoot.addEventListener('keydown', this._handleKeydownBound);
  }

  disconnectedCallback(): void {
    if (!this.shadowRoot) {
      return;
    }

    this.shadowRoot.removeEventListener('click', this._handleClickBound);
    this.shadowRoot.removeEventListener('input', this._handleInputBound);
    this.shadowRoot.removeEventListener('change', this._handleChangeBound);
    this.shadowRoot.removeEventListener('keydown', this._handleKeydownBound);
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) {
      return;
    }

    switch (name) {
      case 'value':
        this._value = newValue || '';
        this._draftValue = this._getDisplayValue();
        this._activeTab = this._inferActiveTab(this._getDisplayValue());
        break;
      case 'readonly':
        this._readonly = newValue !== null;
        break;
    }

    this.render();
  }

  set property(value: string | undefined) {
    this._property = value;
  }

  get property(): string | undefined {
    return this._property;
  }

  set value(value: unknown) {
    this._value = value;
    this._draftValue = this._getDisplayValue();
    this._activeTab = this._inferActiveTab(this._getDisplayValue());
    this.render();
  }

  get value(): unknown {
    return this._value;
  }

  set metadata(value: QdsConfigField | undefined) {
    this._metadata = value;
  }

  get metadata(): QdsConfigField | undefined {
    return this._metadata;
  }

  set context(value: unknown) {
    this._context = value;
  }

  get context(): unknown {
    return this._context;
  }

  set target(value: Record<string, unknown> | undefined) {
    this._target = value;
  }

  get target(): Record<string, unknown> | undefined {
    return this._target;
  }

  set readonly(value: boolean) {
    this._readonly = Boolean(value);
    this.render();
  }

  get readonly(): boolean {
    return this._readonly;
  }

  /** Opens the picker dialog while preserving focus in an owning editor. */
  open(): void {
    this._openModal();
  }

  private _getDisplayValue(): string {
    const rawValue = typeof this._value === 'string' ? this._value.trim() : '';
    return rawValue || DEFAULT_COLOR_VALUE;
  }

  private _getDraftDisplayValue(): string {
    const rawValue = typeof this._draftValue === 'string' ? this._draftValue.trim() : '';
    return rawValue || this._getDisplayValue();
  }

  private _inferActiveTab(value: string): 'theme' | 'custom' {
    return this._matchThemeToken(value) ? 'theme' : 'custom';
  }

  private _matchThemeToken(value: string): ThemeColorOption | undefined {
    const variableName = extractCssVariableName(value);
    if (variableName) {
      return THEME_COLOR_OPTIONS.find((option) => option.token === variableName);
    }

    return THEME_COLOR_OPTIONS.find((option) => option.value === value.trim());
  }

  private _getPreviewColor(): string {
    const displayValue = this._getDisplayValue();
    return resolveColorExpression(displayValue)
      || resolveColorExpression(DEFAULT_COLOR_VALUE)
      || DEFAULT_COLOR_HEX;
  }

  private _getDraftPreviewColor(): string {
    const displayValue = this._getDraftDisplayValue();
    return resolveColorExpression(displayValue)
      || resolveColorExpression(DEFAULT_COLOR_VALUE)
      || DEFAULT_COLOR_HEX;
  }

  private _getColorInputValue(): string {
    const displayValue = this._getDraftDisplayValue();
    return normalizeHexColor(displayValue)
      || normalizeHexColor(resolveColorExpression(displayValue) || '')
      || normalizeHexColor(resolveColorExpression(DEFAULT_COLOR_VALUE) || '')
      || DEFAULT_COLOR_HEX;
  }

  private _openModal(): void {
    if (this._readonly) {
      return;
    }

    this._open = true;
    this._draftValue = this._getDisplayValue();
    this._activeTab = this._inferActiveTab(this._draftValue);
    this.render();
  }

  private _closeModal(): void {
    this._open = false;
    this._draftValue = this._getDisplayValue();
    this.render();
  }

  private _applyDraft(): void {
    this._value = this._getDraftDisplayValue();
    this._open = false;
    this.render();
    this.dispatchEvent(new CustomEvent('value-change', {
      detail: { value: this._value },
      bubbles: true,
      composed: true
    }));
  }

  private _setDraftValue(nextValue: string, preferredTab: 'theme' | 'custom', rerender = true): void {
    this._draftValue = nextValue;
    this._activeTab = preferredTab;
    if (rerender) {
      this.render();
    }
  }

  private _updateLivePreview(nextValue: string): void {
    if (!this.shadowRoot) {
      return;
    }

    const previewSwatch = this.shadowRoot.querySelector<HTMLElement>('[data-role="draft-swatch"]');
    if (previewSwatch) {
      previewSwatch.style.backgroundColor = resolveColorExpression(nextValue)
        || resolveColorExpression(DEFAULT_COLOR_VALUE)
        || DEFAULT_COLOR_HEX;
    }

    const previewValue = this.shadowRoot.querySelector<HTMLElement>('.color-picker__modal-value');
    if (previewValue) {
      previewValue.textContent = nextValue;
      previewValue.setAttribute('title', nextValue);
    }
  }

  private _handleClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    const actionButton = target?.closest<HTMLElement>('[data-action]');
    const action = actionButton?.dataset.action;

    if (action === 'open-modal') {
      this._openModal();
      return;
    }

    if (action === 'cancel') {
      this._closeModal();
      return;
    }

    if (action === 'apply') {
      this._applyDraft();
      return;
    }

    if (action === 'close-modal') {
      this._closeModal();
      return;
    }

    if (action === 'backdrop' && target === actionButton) {
      this._closeModal();
      return;
    }

    if (this._readonly) {
      return;
    }

    const tabButton = target?.closest<HTMLButtonElement>('[data-tab]');
    if (tabButton?.dataset.tab === 'theme' || tabButton?.dataset.tab === 'custom') {
      this._activeTab = tabButton.dataset.tab;
      this.render();
      return;
    }

    const swatchButton = target?.closest<HTMLButtonElement>('[data-token-value]');
    if (swatchButton?.dataset.tokenValue) {
      this._setDraftValue(swatchButton.dataset.tokenValue, 'theme');
    }
  }

  private _handleInput(event: Event): void {
    if (this._readonly) {
      return;
    }

    const target = event.target as HTMLInputElement | null;
    if (target?.dataset.role === 'custom-input' && typeof target.value === 'string') {
      this._draftValue = target.value;
      this._activeTab = 'custom';
      this._updateLivePreview(target.value);
    }
  }

  private _handleChange(event: Event): void {
    const target = event.target as HTMLInputElement | null;
    if (target?.dataset.role === 'custom-input' && typeof target.value === 'string') {
      this._draftValue = target.value;
      this._activeTab = 'custom';
    }
  }

  private _handleKeydown(event: KeyboardEvent): void {
    if (!this._open) {
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      this._closeModal();
    }
  }

  render(): void {
    if (!this.shadowRoot) {
      return;
    }

    const displayValue = this._getDisplayValue();
    const draftValue = this._getDraftDisplayValue();
    const selectedToken = this._matchThemeToken(draftValue);
    const disabledAttribute = this._readonly ? 'disabled' : '';

    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: block;
          width: 100%;
          min-width: 0;
          font-family: var(--theme-font-family, var(--qds-font-family, 'IBM Plex Sans', sans-serif));
          color: var(--theme-text-primary, #333333);
        }

        *, *::before, *::after {
          box-sizing: border-box;
        }

        .color-picker {
          display: flex;
          justify-content: flex-end;
          position: relative;
          width: 100%;
          min-width: 0;
        }

        .color-picker__trigger {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 44px;
          min-width: 44px;
          min-height: 40px;
          padding: var(--theme-space-1, 4px);
          border: 1px solid var(--theme-border-primary, #E4E9F2);
          border-radius: var(--theme-radius-sm, 4px);
          background: var(--theme-background-primary, #FFFFFF);
          color: inherit;
          cursor: pointer;
          text-align: left;
          transition: border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease;
        }

        .color-picker__trigger:hover:not(:disabled) {
          border-color: var(--theme-border-strong, #AFBBD1);
          background: var(--theme-background-secondary, #F8F9FA);
        }

        .color-picker__trigger:focus-visible {
          outline: 2px solid var(--button-focus-ring, rgba(68, 94, 137, 0.18));
          outline-offset: 2px;
        }

        .color-picker__trigger:disabled {
          cursor: default;
          opacity: 0.6;
        }

        /* Toolbar consumers need the swatch to read as one formatting control,
           rather than as a standalone field button. */
        :host([toolbar]) {
          display: inline-flex;
          width: 32px;
          min-width: 32px;
          vertical-align: middle;
        }

        :host([toolbar]) .color-picker {
          width: 32px;
        }

        :host([toolbar]) .color-picker__trigger {
          width: 32px;
          min-width: 32px;
          min-height: 32px;
          height: 32px;
          padding: 4px;
          border-color: transparent;
          background: transparent;
          box-shadow: none;
        }

        :host([toolbar]) .color-picker__trigger:hover:not(:disabled) {
          border-color: transparent;
          background: var(--button-hover-bg, rgba(0, 0, 0, 0.05));
        }

        :host([toolbar]) .color-picker__current-swatch {
          width: 20px;
          height: 20px;
          flex-basis: 20px;
        }

        .color-picker__current {
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .color-picker__current-swatch {
          width: 28px;
          height: 28px;
          flex: 0 0 28px;
          border: 1px solid var(--theme-border-primary, #E4E9F2);
          border-radius: var(--theme-radius-sm, 4px);
          box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.4);
        }

        .color-picker__backdrop {
          position: fixed;
          inset: 0;
          display: none;
          align-items: center;
          justify-content: center;
          padding: var(--theme-space-5, 20px);
          background: var(--theme-background-overlay-subtle, rgba(255, 255, 255, 0.9));
          z-index: 1000;
        }

        .color-picker__backdrop[data-open="true"] {
          display: flex;
        }

        .color-picker__modal {
          display: grid;
          gap: var(--theme-space-3, 12px);
          width: min(560px, 100%);
          max-height: min(80vh, 720px);
          padding: var(--theme-space-4, 16px);
          border: 1px solid var(--theme-border-primary, #E4E9F2);
          border-radius: var(--theme-radius-md, 8px);
          background: var(--theme-background-primary, #FFFFFF);
          box-shadow: var(--theme-shadow-md, 0 8px 24px rgba(44, 51, 73, 0.1));
          overflow: auto;
        }

        .color-picker__modal-header,
        .color-picker__modal-footer {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: var(--theme-space-2, 8px);
        }

        .color-picker__modal-title {
          margin: 0;
          font-size: var(--theme-font-size-sm, 14px);
          font-weight: var(--theme-font-weight-semibold, 600);
          color: var(--theme-text-primary, #333333);
        }

        .color-picker__modal-close,
        .color-picker__modal-button {
          appearance: none;
          border: 1px solid var(--theme-border-primary, #E4E9F2);
          border-radius: var(--theme-radius-sm, 4px);
          background: var(--theme-background-primary, #FFFFFF);
          color: var(--theme-text-primary, #333333);
          cursor: pointer;
          font: inherit;
          min-height: 36px;
          padding: 0 12px;
        }

        .color-picker__modal-button[data-variant="primary"] {
          border-color: var(--theme-accent-primary, #445E89);
          background: var(--theme-accent-primary, #445E89);
          color: var(--theme-text-on-accent-primary, #FFFFFF);
        }

        .color-picker__modal-current {
          display: flex;
          align-items: center;
          gap: var(--theme-space-2, 8px);
          min-width: 0;
        }

        .color-picker__modal-value {
          min-width: 0;
          margin: 0;
          padding: 0 var(--theme-space-2, 8px);
          border-radius: var(--theme-radius-sm, 4px);
          background: var(--theme-background-secondary, #F8F9FA);
          color: var(--theme-text-secondary, #555555);
          font-family: var(--theme-font-family-mono, ui-monospace, SFMono-Regular, Menlo, monospace);
          font-size: var(--theme-font-size-xs, 12px);
          line-height: 32px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          flex: 1 1 auto;
        }

        .color-picker__tabs {
          display: inline-flex;
          gap: var(--theme-space-1, 4px);
          padding: var(--theme-space-1, 4px);
          border: 1px solid var(--theme-border-primary, #E4E9F2);
          border-radius: var(--theme-radius-sm, 4px);
          background: var(--theme-background-secondary, #F8F9FA);
          width: fit-content;
          max-width: 100%;
        }

        .color-picker__tab {
          appearance: none;
          border: 0;
          border-radius: var(--theme-radius-sm, 4px);
          background: transparent;
          color: var(--theme-text-secondary, #555555);
          cursor: pointer;
          font: inherit;
          font-size: var(--theme-font-size-xs, 12px);
          font-weight: var(--theme-font-weight-medium, 500);
          line-height: 1;
          padding: 7px 10px;
          transition: background-color 0.2s ease, color 0.2s ease;
        }

        .color-picker__tab[aria-selected="true"] {
          background: var(--theme-background-primary, #FFFFFF);
          color: var(--theme-text-primary, #333333);
          box-shadow: var(--theme-shadow-xs, 0 1px 2px rgba(44, 51, 73, 0.1));
        }

        .color-picker__tab:disabled {
          cursor: default;
          opacity: 0.55;
        }

        .color-picker__panel {
          display: none;
        }

        .color-picker__panel[data-active="true"] {
          display: block;
        }

        .color-picker__swatches {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(28px, 1fr));
          gap: var(--theme-space-2, 8px);
        }

        .color-picker__swatch {
          appearance: none;
          border: 1px solid var(--theme-border-primary, #E4E9F2);
          border-radius: var(--theme-radius-sm, 4px);
          background: transparent;
          cursor: pointer;
          min-height: 28px;
          padding: 2px;
          transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
        }

        .color-picker__swatch:hover:not(:disabled) {
          border-color: var(--theme-border-strong, #AFBBD1);
          transform: translateY(-1px);
        }

        .color-picker__swatch[data-selected="true"] {
          border-color: var(--theme-accent-primary, #445E89);
          box-shadow: 0 0 0 1px var(--theme-accent-primary, #445E89);
        }

        .color-picker__swatch:disabled {
          cursor: default;
          opacity: 0.55;
        }

        .color-picker__swatch-fill {
          display: block;
          width: 100%;
          height: 100%;
          min-height: 22px;
          border-radius: 2px;
        }

        .color-picker__custom {
          display: flex;
          align-items: center;
          gap: var(--theme-space-2, 8px);
        }

        .color-picker__native {
          width: 100%;
          min-width: 0;
          height: 40px;
          padding: 0;
          border: 1px solid var(--theme-border-primary, #E4E9F2);
          border-radius: var(--theme-radius-sm, 4px);
          background: var(--theme-background-primary, #FFFFFF);
          cursor: pointer;
        }

        .color-picker__native:disabled {
          cursor: default;
          opacity: 0.55;
        }
      </style>
      <div class="color-picker">
        <button
          type="button"
          class="color-picker__trigger"
          data-action="open-modal"
          aria-haspopup="dialog"
          aria-expanded="${this._open}"
          aria-label="${escapeHtml(`${this._metadata?.displayName || 'Color'}: ${displayValue}`)}"
          title="${escapeHtml(displayValue)}"
          ${disabledAttribute}
        >
          <span class="color-picker__current">
            <span class="color-picker__current-swatch" data-role="current-swatch" part="current-swatch" aria-hidden="true"></span>
          </span>
        </button>
        <div class="color-picker__backdrop" data-action="backdrop" data-open="${this._open}" ${this._open ? '' : 'aria-hidden="true"'}>
          <div class="color-picker__modal" data-role="modal" role="dialog" aria-modal="true" aria-label="${escapeHtml(this._metadata?.displayName || 'Choose color')}">
            <div class="color-picker__modal-header">
              <h3 class="color-picker__modal-title">${escapeHtml(this._metadata?.displayName || 'Choose color')}</h3>
              <button type="button" class="color-picker__modal-close" data-action="close-modal">Close</button>
            </div>
            <div class="color-picker__modal-current">
              <span class="color-picker__current-swatch" data-role="draft-swatch"></span>
              <code class="color-picker__modal-value" title="${escapeHtml(draftValue)}">${escapeHtml(draftValue)}</code>
            </div>
            <div class="color-picker__tabs" role="tablist" aria-label="Color source">
              <button
                type="button"
                class="color-picker__tab"
                data-tab="theme"
                role="tab"
                aria-selected="${this._activeTab === 'theme'}"
                ${disabledAttribute}
              >Theme</button>
              <button
                type="button"
                class="color-picker__tab"
                data-tab="custom"
                role="tab"
                aria-selected="${this._activeTab === 'custom'}"
                ${disabledAttribute}
              >Custom</button>
            </div>
            <div class="color-picker__panel" data-panel="theme" data-active="${this._activeTab === 'theme'}">
              <div class="color-picker__swatches">
                ${THEME_COLOR_OPTIONS.map((option) => `
                  <button
                    type="button"
                    class="color-picker__swatch"
                    data-token="${option.token}"
                    data-token-value="${option.value}"
                    data-selected="${selectedToken?.token === option.token}"
                    title="${option.token}"
                    aria-label="${escapeHtml(option.label)}"
                    ${disabledAttribute}
                  >
                    <span class="color-picker__swatch-fill" style="background: ${option.value};"></span>
                  </button>
                `).join('')}
              </div>
            </div>
            <div class="color-picker__panel" data-panel="custom" data-active="${this._activeTab === 'custom'}">
              <div class="color-picker__custom">
                <input
                  type="color"
                  class="color-picker__native"
                  data-role="custom-input"
                  value="${this._getColorInputValue()}"
                  aria-label="Custom color"
                  ${disabledAttribute}
                />
              </div>
            </div>
            <div class="color-picker__modal-footer">
              <button type="button" class="color-picker__modal-button" data-action="cancel">Cancel</button>
              <button type="button" class="color-picker__modal-button" data-action="apply" data-variant="primary">Apply</button>
            </div>
          </div>
        </div>
      </div>
    `;

    const currentSwatch = this.shadowRoot.querySelector<HTMLElement>('[data-role="current-swatch"]');
    if (currentSwatch) {
      currentSwatch.style.backgroundColor = this._getPreviewColor();
    }

    const draftSwatch = this.shadowRoot.querySelector<HTMLElement>('[data-role="draft-swatch"]');
    if (draftSwatch) {
      draftSwatch.style.backgroundColor = this._getDraftPreviewColor();
    }
  }
}

if (!customElements.get('qds-color-picker')) {
  customElements.define('qds-color-picker', QdsColorPicker);
}

export default QdsColorPicker;
